import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, getToken, API_BASE } from '../services/apiClient';
import { JHA_TYPES, JHA_TEMPLATES } from '../jhaTemplates';
import { SignaturePad } from './SafetyTraining';
import JobSearchSelect from './JobSearchSelect';

// Field supervisor: create a JHA for a job, review it with the crew, collect
// each person's signature on this phone, then share the signed PDF.
export default function JhaField() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projectId, setProjectId] = useState(params.get('project') || '');
  const [projects, setProjects] = useState([]);
  const [mode, setMode] = useState('list');           // list | create | detail
  const [jhas, setJhas] = useState([]);
  const [current, setCurrent] = useState(null);        // full JHA (with signatures) in detail
  const [task, setTask] = useState('');
  const [title, setTitle] = useState('');
  const [steps, setSteps] = useState([]);
  const [signName, setSignName] = useState('');
  const [showSig, setShowSig] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  function loadList() { apiFetch(`/jha?projectId=${projectId}`).then(d => setJhas(Array.isArray(d) ? d : [])).catch(() => {}); }
  useEffect(() => { apiFetch('/my-projects').then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {}); }, []);
  useEffect(() => { loadList(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  function pickTask(t) {
    setTask(t); if (!title || JHA_TYPES.includes(title)) setTitle(t);
    const tmpl = JHA_TEMPLATES[t]; setSteps(tmpl ? tmpl.map(s => ({ ...s })) : [{ step: '', hazard: '', control: '' }]);
  }
  async function createJha() {
    if (!projectId) { setErr('Pick a job first'); return; }
    if (!task) { setErr('Pick a task type'); return; }
    setBusy(true); setErr('');
    try {
      const saved = await apiFetch('/jha', { method: 'POST', body: JSON.stringify({ title: title || task, task, project_id: projectId || null, steps: steps.filter(s => s.step || s.hazard || s.control) }) });
      const full = await apiFetch(`/jha/${saved.id}`);
      setCurrent(full); setMode('detail');
    } catch (e) { setErr('Could not create'); } finally { setBusy(false); }
  }
  async function openJha(id) { try { setCurrent(await apiFetch(`/jha/${id}`)); setMode('detail'); } catch {} }
  async function handleSign(sig) {
    setShowSig(false); setBusy(true);
    try {
      await apiFetch(`/jha/${current.id}/sign`, { method: 'POST', body: JSON.stringify({ name: signName.trim(), signature: sig }) });
      setSignName(''); setCurrent(await apiFetch(`/jha/${current.id}`));
    } catch (e) { alert('Could not save signature'); } finally { setBusy(false); }
  }
  async function sharePdf() {
    try {
      const res = await fetch(`${API_BASE}/jha/${current.id}/pdf`, { headers: { 'x-worker-token': getToken() } });
      const blob = await res.blob();
      const file = new File([blob], `JHA - ${current.task || 'JHA'}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Job Hazard Analysis' });
      } else {
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name; a.click(); URL.revokeObjectURL(url);
      }
    } catch (e) { alert('Could not generate PDF'); }
  }

  const inp = { border: '1px solid var(--border, #e5e7eb)', borderRadius: 10, padding: '12px', fontSize: 16, width: '100%', boxSizing: 'border-box', background: 'var(--card, #fff)', color: 'var(--text)' };
  const lbl = { fontSize: 12, fontWeight: 700, color: 'var(--gray-muted, #6b7280)', textTransform: 'uppercase', marginBottom: 6, display: 'block' };
  const setStep = (i, k, v) => setSteps(s => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));

  const Header = ({ onBack, title: t }) => (
    <div className="top-bar">
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: '#fff', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{t}</span>
      </button>
    </div>
  );

  // ── Detail / signing ──
  if (mode === 'detail' && current) {
    let stp = []; try { stp = JSON.parse(current.steps || '[]'); } catch {}
    return (
      <div className="page">
        <Header onBack={() => { setMode('list'); loadList(); }} title="JHAs" />
        <div style={{ padding: '18px 16px 100px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#b45309' }}>⚠️ {current.task || 'JHA'}</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)', margin: '2px 0 14px' }}>{current.title}</div>

          {stp.map((s, i) => (
            <div key={i} style={{ background: 'var(--card,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 12, padding: 12, marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{i + 1}. {s.step}</div>
              {s.hazard && <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 4 }}><b>Hazard:</b> {s.hazard}</div>}
              {s.control && <div style={{ fontSize: 13, color: '#15803d', marginTop: 2 }}><b>Control:</b> {s.control}</div>}
            </div>
          ))}

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-muted)', textTransform: 'uppercase', margin: '18px 0 8px' }}>Crew Sign-Off ({(current.signatures || []).length})</div>
          {(current.signatures || []).map(s => (
            <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--card,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{s.name || '—'}</div>
              {s.signature && <img src={s.signature} alt="" style={{ height: 30 }} />}
            </div>
          ))}

          <div style={{ background: 'var(--card,#fff)', border: '1px dashed #cbd5e1', borderRadius: 12, padding: 12, marginTop: 8 }}>
            <label style={lbl}>Add crew signature</label>
            <input value={signName} onChange={e => setSignName(e.target.value)} placeholder="Crew member name" style={inp} />
            <button onClick={() => { if (!signName.trim()) { alert('Enter a name first'); return; } setShowSig(true); }} disabled={busy}
              style={{ width: '100%', marginTop: 8, padding: 14, background: '#e87722', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>✍️ Sign</button>
          </div>

          <button onClick={sharePdf} style={{ width: '100%', marginTop: 18, padding: 16, background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>📄 Share signed PDF</button>
        </div>
        {showSig && <SignaturePad onSave={handleSign} onCancel={() => setShowSig(false)} />}
      </div>
    );
  }

  // ── Create ──
  if (mode === 'create') {
    return (
      <div className="page">
        <Header onBack={() => setMode('list')} title="JHAs" />
        <div style={{ padding: '18px 16px 100px' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)', marginBottom: 14 }}>⚠️ New JHA</div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Job</label>
            <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={lbl}>Task type</label>
            <select value={task} onChange={e => pickTask(e.target.value)} style={inp}>
              <option value="">— Select task —</option>
              {JHA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {task && (
            <>
              <div style={{ marginBottom: 12 }}><label style={lbl}>Title</label><input value={title} onChange={e => setTitle(e.target.value)} style={inp} /></div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-muted)', textTransform: 'uppercase', margin: '8px 0' }}>Steps · Hazards · Controls</div>
              {steps.map((s, i) => (
                <div key={i} style={{ background: 'var(--card,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 12, padding: 10, marginBottom: 8 }}>
                  <input value={s.step} onChange={e => setStep(i, 'step', e.target.value)} placeholder={`Step ${i + 1}`} style={{ ...inp, marginBottom: 6, fontWeight: 600 }} />
                  <input value={s.hazard} onChange={e => setStep(i, 'hazard', e.target.value)} placeholder="Hazard" style={{ ...inp, marginBottom: 6 }} />
                  <input value={s.control} onChange={e => setStep(i, 'control', e.target.value)} placeholder="Control" style={inp} />
                </div>
              ))}
              <button onClick={() => setSteps(s => [...s, { step: '', hazard: '', control: '' }])} style={{ padding: '10px 14px', borderRadius: 10, border: '1px dashed #cbd5e1', background: 'var(--card,#fff)', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>+ Add step</button>
              {err && <div style={{ color: '#dc2626', fontSize: 14, margin: '12px 0' }}>{err}</div>}
              <button onClick={createJha} disabled={busy} style={{ width: '100%', marginTop: 16, padding: 16, background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Creating…' : 'Create & Review with Crew'}</button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── List ──
  return (
    <div className="page">
      <Header onBack={() => navigate(-1)} title="Back" />
      <div style={{ padding: '18px 16px 100px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>⚠️ Job Hazard Analyses</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Job</label>
          <JobSearchSelect value={projectId} projects={projects} onChange={setProjectId} />
        </div>
        <button onClick={() => { setTask(''); setTitle(''); setSteps([]); setErr(''); setMode('create'); }}
          style={{ width: '100%', padding: 14, background: '#e87722', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>+ New JHA</button>
        {jhas.length === 0 ? <div style={{ color: 'var(--gray-muted)', fontSize: 14, textAlign: 'center', padding: 24 }}>No JHAs for this job yet.</div>
          : jhas.map(j => (
            <button key={j.id} onClick={() => openJha(j.id)} style={{ width: '100%', textAlign: 'left', background: 'var(--card,#fff)', border: '1px solid var(--border,#e5e7eb)', borderRadius: 12, padding: 14, marginBottom: 10, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{j.title}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-muted)' }}>{j.task || ''} · {j.signed_count} signed</div>
              </div>
              <span style={{ color: '#e87722', fontSize: 18 }}>→</span>
            </button>
          ))}
      </div>
    </div>
  );
}
